"use client";

import { SAFE_MARKDOWN_TRANSFORMERS } from "@/components/drops/create/lexical/transformers/markdownTransformers";
import type { CreateDropConfig, CreateDropPart, CreateDropRequestPart, MentionedUser, MentionedWave, ReferencedNft } from "@/entities/IDrop";
import type { ApiCreateDropRequest } from "@/generated/models/ApiCreateDropRequest";
import type { ApiDropMentionedUser } from "@/generated/models/ApiDropMentionedUser";
import type { ApiMentionedWave } from "@/generated/models/ApiMentionedWave";
import { ApiDropType } from "@/generated/models/ApiDropType";
import type { ApiWave } from "@/generated/models/ApiWave";
import { ApiWaveMetadataType } from "@/generated/models/ApiWaveMetadataType";
import { ApiWaveType } from "@/generated/models/ApiWaveType";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import type { DropPrivileges } from "@/hooks/useDropPriviledges";
import { selectEditingDropId } from "@/store/editSlice";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import { ActiveDropAction } from "@/types/dropInteractionTypes";
import { AnimatePresence, motion } from "framer-motion";
import type { EditorState } from "lexical";
import dynamic from "next/dynamic";
import React, { memo, useCallback, useContext, useEffect, useMemo, useLayoutEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useAuth } from "../auth/Auth";
import { HASHTAG_TRANSFORMER } from "../drops/create/lexical/transformers/HastagTransformer";
import { IMAGE_TRANSFORMER } from "../drops/create/lexical/transformers/ImageTransformer";
import { MENTION_TRANSFORMER } from "../drops/create/lexical/transformers/MentionTransformer";
import { WAVE_MENTION_TRANSFORMER } from "../drops/create/lexical/transformers/WaveMentionTransformer";
import { ReactQueryWrapperContext } from "../react-query-wrapper/ReactQueryWrapper";
import CreateDropActions from "./CreateDropActions";
import { CreateDropContentFiles } from "./CreateDropContentFiles";
import CreateDropContentRequirements from "./CreateDropContentRequirements";
import { CreateDropDropModeToggle } from "./CreateDropDropModeToggle";
import type { CreateDropInputHandles } from "./CreateDropInput";
import CreateDropInput from "./CreateDropInput";
import CreateDropMetadata from "./CreateDropMetadata";
import CreateDropReplyingWrapper from "./CreateDropReplyingWrapper";
import { CreateDropSubmit } from "./CreateDropSubmit";
import { exportDropMarkdown } from "@/components/waves/drops/normalizeDropMarkdown";
import { ProcessIncomingDropType } from "@/contexts/wave/hooks/useWaveRealtimeUpdater";
import { useMyStream } from "@/contexts/wave/MyStreamContext";
import { MAX_DROP_UPLOAD_FILES } from "@/helpers/Helpers";
import { WsMessageType } from "@/helpers/Types";
import { isReservedIdentitySubmissionMetadataKey } from "@/helpers/waves/identity-submission-metadata";
import { useDropSignature } from "@/hooks/drops/useDropSignature";
import { WaveSubmissionExperience } from "@/helpers/waves/wave-submission-experience.helpers";
import { useWebSocket } from "@/services/websocket";
import throttle from "lodash/throttle";
import { useSeizeConnectContext } from "../auth/SeizeConnectContext";
import CreateDropIdentityField from "./CreateDropIdentityField";
import CreateDropIdentityPickerModal from "./CreateDropIdentityPickerModal";
import { EMOJI_TRANSFORMER } from "../drops/create/lexical/transformers/EmojiTransformer";
import { multiPartUpload } from "./create-wave/services/multiPartUpload";
import type { DropMutationBody } from "./CreateDrop";
import { generateMetadataId, useDropMetadata } from "./hooks/useDropMetadata";
import { hasCurrentDropPartContent, shouldUseInitialDropConfig } from "./utils/createDropContentSubmission";
import type { MissingRequirements } from "./utils/getMissingRequirements";
import { getMissingRequirements } from "./utils/getMissingRequirements";
import { getOptimisticDrop } from "./utils/getOptimisticDrop";
import { buildDropSubmissionMetadata } from "./utils/buildDropSubmissionMetadata";
import { getIdentitySubmissionMetadataErrors } from "./utils/identitySubmissionMetadataValidation";
import { getEffectiveIdentitySubmitAttempt, getEffectiveSelectedIdentity, getIdentitySubmissionScopeKey } from "./utils/identitySubmissionState";
import { normalizeCurationDropInput } from "./utils/validateCurationDropUrl";
import { getSelectableIdentityOption, type SelectableIdentityOption } from "../utils/input/profile-search/getSelectableIdentity";
import { ApiWaveParticipationIdentitySubmissionWhoCanBeSubmitted } from "@/generated/models/ApiWaveParticipationIdentitySubmissionWhoCanBeSubmitted";

// Use next/dynamic for lazy loading with SSR support
const TermsSignatureFlow = dynamic(() => import("../terms/TermsSignatureFlow"), {
  loading: () => null
});
export type CreateDropMetadataType = {
  readonly id: string;
  key: string;
  readonly type: ApiWaveMetadataType.String;
  value: string | null;
  readonly required: boolean;
} | {
  readonly id: string;
  key: string;
  readonly type: ApiWaveMetadataType.Number;
  value: number | null;
  readonly required: boolean;
} | {
  readonly id: string;
  key: string;
  readonly type: null;
  value: string | null;
  readonly required: boolean;
};
type ScopedValueState<T> = {
  readonly scopeKey: string;
  readonly value: T;
};
interface CreateDropContentProps {
  readonly activeDrop: ActiveDropState | null;
  readonly onCancelReplyQuote: () => void;
  readonly wave: ApiWave;
  readonly drop: CreateDropConfig | null;
  readonly isStormMode: boolean;
  readonly isDropMode: boolean;
  readonly dropId: string | null;
  readonly setDrop: React.Dispatch<React.SetStateAction<CreateDropConfig | null>>;
  readonly setIsStormMode: React.Dispatch<React.SetStateAction<boolean>>;
  readonly onDropModeChange: (newIsDropMode: boolean) => void;
  readonly onSwitchToDropModeWithUrl: (url: string) => void;
  readonly submitDrop: (dropRequest: DropMutationBody) => void;
  readonly privileges: DropPrivileges;
  readonly showDropModeToggle: boolean;
  readonly dropModeToggleExitLabel: string | null;
  readonly canExitDropMode: boolean;
  readonly submissionExperience: WaveSubmissionExperience;
}
const CONTAINER_WIDTH_THRESHOLD = 500;
const SELECT_OTHER_IDENTITY_ERROR = "Select someone else to nominate.";
const normalizeIdentityValue = (identity: string | null | undefined) => identity?.trim().toLowerCase() ?? null;
const isMetadataValuePresent = (value: string | number | null): boolean => {
  if (value === null) {
    return false;
  }
  if (typeof value === "string") {
    return value.trim().length > 0;
  }
  return true;
};
const hasMetadataContent = (metadata: CreateDropMetadataType[]): boolean => metadata.some(item => isMetadataValuePresent(item.value));
const hasSubmissionContent = ({
  markdown,
  files,
  parts,
  hasMetadata
}: {
  readonly markdown: string | null;
  readonly files: File[];
  readonly parts: CreateDropPart[];
  readonly hasMetadata: boolean;
}): boolean => {
  if (markdown && markdown.trim().length > 0) {
    return true;
  }
  if (files.length > 0) {
    return true;
  }
  if (parts.length > 0) {
    return true;
  }
  return hasMetadata;
};
const ensurePartsWithFallback = (parts: CreateDropPart[], shouldAddPlaceholder: boolean): CreateDropPart[] => {
  if (parts.length > 0 || !shouldAddPlaceholder) {
    return parts;
  }
  return [{
    content: null,
    quoted_drop: null,
    media: []
  }];
};
const getPartMentions = (markdown: string | null, mentionedUsers: Omit<MentionedUser, "current_handle">[]) => {
  return mentionedUsers.filter(user => markdown?.includes(`@[${user.handle_in_content}]`));
};
const getUpdatedMentions = (partMentions: Omit<MentionedUser, "current_handle">[], existingMentions: ApiDropMentionedUser[]) => {
  const notAddedMentions = partMentions.filter(mention => !existingMentions.some(existing => existing.mentioned_profile_id === mention.mentioned_profile_id));
  return [...existingMentions, ...notAddedMentions];
};
const getPartNfts = (markdown: string | null, referencedNfts: ReferencedNft[]) => {
  return referencedNfts.filter(nft => markdown?.includes(`$[${nft.name}]`));
};
const getUpdatedNfts = (partNfts: ReferencedNft[], existingNfts: ReferencedNft[]) => {
  const notAddedNfts = partNfts.filter(nft => !existingNfts.some(existing => existing.contract === nft.contract && existing.token === nft.token));
  return [...existingNfts, ...notAddedNfts];
};
const getPartWaves = (markdown: string | null, mentionedWaves: MentionedWave[]) => mentionedWaves.filter(wave => markdown?.includes(`#[${wave.wave_name_in_content}]`));
const getUpdatedWaves = (partWaves: MentionedWave[], existingWaves: ApiMentionedWave[]) => {
  const notAddedWaves = partWaves.filter(wave => !existingWaves.some(existing => existing.wave_id === wave.wave_id));
  return [...existingWaves, ...notAddedWaves];
};
type HandleDropPartResult = {
  updatedMentions: ApiDropMentionedUser[];
  updatedNfts: ReferencedNft[];
  updatedWaves: ApiMentionedWave[];
  updatedMarkdown: string;
};
const handleDropPart = (markdown: string | null, existingMentions: ApiDropMentionedUser[], existingNfts: ReferencedNft[], existingWaves: ApiMentionedWave[], mentionedUsers: Omit<MentionedUser, "current_handle">[], referencedNfts: ReferencedNft[], mentionedWaves: MentionedWave[]): HandleDropPartResult => {
  const partMentions = getPartMentions(markdown, mentionedUsers);
  const updatedMentions = getUpdatedMentions(partMentions, existingMentions);
  const partNfts = getPartNfts(markdown, referencedNfts);
  const updatedNfts = getUpdatedNfts(partNfts, existingNfts);
  const partWaves = getPartWaves(markdown, mentionedWaves);
  const updatedWaves = getUpdatedWaves(partWaves, existingWaves);
  const updatedMarkdown = markdown ?? "";
  return {
    updatedMentions,
    updatedNfts,
    updatedWaves,
    updatedMarkdown
  };
};
export interface UploadingFile {
  file: File;
  isUploading: boolean;
  progress: number;
}
const generateMediaForPart = async (media: File, setUploadingFiles: React.Dispatch<React.SetStateAction<UploadingFile[]>>) => {
  setUploadingFiles(prev => [...prev, {
    file: media,
    isUploading: true,
    progress: 0
  }]);
  return await multiPartUpload({
    file: media,
    path: "drop",
    onProgress: progress => setUploadingFiles(prev => prev.map(uf => uf.file === media ? {
      ...uf,
      progress
    } : uf))
  }).finally(() => {
    setUploadingFiles(prev => prev.filter(uf => uf.file !== media));
  });
};
const generatePart = async (part: CreateDropPart, setUploadingFiles: React.Dispatch<React.SetStateAction<UploadingFile[]>>): Promise<CreateDropRequestPart> => {
  const media = await Promise.all(part.media.map(media => generateMediaForPart(media, setUploadingFiles)));
  return {
    ...part,
    media
  };
};
const generateParts = async (parts: CreateDropPart[], setUploadingFiles: React.Dispatch<React.SetStateAction<UploadingFile[]>>): Promise<CreateDropRequestPart[]> => {
  try {
    return await Promise.all(parts.map(part => generatePart(part, setUploadingFiles)));
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    if (message.includes("content_type")) {
      throw new Error("File type not supported. Please use MP4 for videos.");
    }
    throw new Error("Error uploading file. Please try again.");
  }
};
const CreateDropContent: React.FC<CreateDropContentProps> = ({
  activeDrop,
  onCancelReplyQuote,
  wave,
  drop,
  isStormMode,
  isDropMode,
  dropId,
  setDrop,
  setIsStormMode,
  onDropModeChange,
  onSwitchToDropModeWithUrl,
  submitDrop,
  privileges,
  showDropModeToggle,
  dropModeToggleExitLabel,
  canExitDropMode,
  submissionExperience
}) => {
  const {
    isSafeWallet,
    address
  } = useSeizeConnectContext();
  const {
    send
  } = useWebSocket();
  const {
    isApp
  } = useDeviceInfo();
  const actionsContainerRef = useRef<HTMLDivElement>(null);
  const hasUserToggledOptionsRef = useRef(false);
  const prevWaveIdRef = useRef(wave.id);
  const [isWideContainer, setIsWideContainer] = useState(false);
  const editingDropId = useSelector(selectEditingDropId);
  const {
    requestAuth,
    setToast,
    connectedProfile
  } = useAuth();
  const {
    addOptimisticDrop
  } = useContext(ReactQueryWrapperContext);
  const {
    processIncomingDrop
  } = useMyStream();
  const {
    signDrop
  } = useDropSignature();
  const [submitting, setSubmitting] = useState(false);
  const [editorState, setEditorState] = useState<EditorState | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [metadataOpenState, setMetadataOpenState] = useState<ScopedValueState<boolean> | null>(null);
  const [showOptionsState, setShowOptionsState] = useState<ScopedValueState<boolean> | null>(null);
  const [selectedIdentityState, setSelectedIdentityState] = useState<ScopedValueState<SelectableIdentityOption | null> | null>(null);
  const [hasAttemptedIdentitySubmitState, setHasAttemptedIdentitySubmitState] = useState<ScopedValueState<boolean> | null>(null);
  const [identityPickerOpenState, setIdentityPickerOpenState] = useState<ScopedValueState<boolean> | null>(null);
  const [identityPickerAutoOpenState, setIdentityPickerAutoOpenState] = useState<ScopedValueState<boolean> | null>(null);
  const [identityPickerErrorMessageState, setIdentityPickerErrorMessageState] = useState<ScopedValueState<string | null> | null>(null);
  const closeOnNextInputRef = useRef(false);
  const prevIsDropModeRef = useRef(isDropMode);
  const [dropModeSessionEpoch, setDropModeSessionEpoch] = useState(0);
  const isWaveChanged = prevWaveIdRef.current !== wave.id;
  if (isWaveChanged) {
    prevWaveIdRef.current = wave.id;
    hasUserToggledOptionsRef.current = false;
    closeOnNextInputRef.current = false;
  }
  const dropModeSessionScopeKey = `${wave.id}:drop-mode:${dropModeSessionEpoch}`;
  const showOptions = isWideContainer || (showOptionsState?.scopeKey === wave.id ? showOptionsState.value : false);
  useLayoutEffect(() => {
    const container = actionsContainerRef.current;
    if (!container) return;
    const measureWidth = () => {
      const width = container.getBoundingClientRect().width;
      const isWide = width >= CONTAINER_WIDTH_THRESHOLD;
      setIsWideContainer(prev => prev === isWide ? prev : isWide);
    };
    measureWidth();
    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) {
        const width_0 = entry.contentRect.width;
        const isWide_0 = width_0 >= CONTAINER_WIDTH_THRESHOLD;
        setIsWideContainer(prev_0 => prev_0 === isWide_0 ? prev_0 : isWide_0);
      }
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);
  useLayoutEffect(() => {
    if (prevIsDropModeRef.current && !isDropMode) {
      setDropModeSessionEpoch(prev_1 => prev_1 + 1);
    }
    prevIsDropModeRef.current = isDropMode;
  }, [isDropMode]);
  const isParticipatory = wave.wave.type !== ApiWaveType.Chat;
  const isIdentitySubmissionExperience = submissionExperience === WaveSubmissionExperience.IDENTITY;
  const isCurationSubmissionExperience = submissionExperience === WaveSubmissionExperience.CURATION_LEGACY;
  const identitySubmissionMode = isIdentitySubmissionExperience ? wave.participation.submission_strategy?.config.who_can_be_submitted ?? null : null;
  const viewerIdentity = getSelectableIdentityOption(connectedProfile);
  const viewerSelectableIdentity = viewerIdentity?.value ?? null;
  const identitySubmissionScopeKey = getIdentitySubmissionScopeKey({
    waveId: wave.id,
    isIdentitySubmissionExperience,
    identitySubmissionMode
  });
  const identitySubmissionSessionScopeKey = `${identitySubmissionScopeKey}:${dropModeSessionEpoch}`;
  const isIdentityPickerAllowed = isIdentitySubmissionExperience && isDropMode && identitySubmissionMode !== null && identitySubmissionMode !== ApiWaveParticipationIdentitySubmissionWhoCanBeSubmitted.OnlyMyself;
  const selectedIdentitySelection = getEffectiveSelectedIdentity({
    isIdentitySubmissionExperience,
    identitySubmissionMode,
    viewerIdentity,
    selectedIdentityState,
    scopeKey: identitySubmissionSessionScopeKey
  });
  const selectedIdentity = selectedIdentitySelection?.value ?? null;
  const hasAttemptedIdentitySubmit = getEffectiveIdentitySubmitAttempt({
    attemptState: hasAttemptedIdentitySubmitState,
    scopeKey: identitySubmissionSessionScopeKey
  });
  const isIdentityPickerExplicitlyOpen = identityPickerOpenState?.scopeKey === identitySubmissionSessionScopeKey ? identityPickerOpenState.value : false;
  const isIdentityPickerAutoOpenAllowed = identityPickerAutoOpenState?.scopeKey === identitySubmissionSessionScopeKey ? identityPickerAutoOpenState.value : true;
  const identityPickerErrorMessage = identityPickerErrorMessageState?.scopeKey === identitySubmissionSessionScopeKey ? identityPickerErrorMessageState.value : null;
  const isIdentityPickerOpen = isIdentityPickerAllowed && (isIdentityPickerExplicitlyOpen || !selectedIdentitySelection && isIdentityPickerAutoOpenAllowed);
  const canDismissIdentityPicker = selectedIdentitySelection !== null || canExitDropMode;
  const isMetadataOpen = isDropMode && (metadataOpenState?.scopeKey === dropModeSessionScopeKey ? metadataOpenState.value : false);
  const requiredMetadata = useMemo(() => {
    if (!isIdentitySubmissionExperience) {
      return wave.participation.required_metadata;
    }
    return wave.participation.required_metadata.filter(item => !isReservedIdentitySubmissionMetadataKey(item.name));
  }, [isIdentitySubmissionExperience, wave.participation.required_metadata]);
  const {
    metadata,
    setMetadata,
    initialMetadata
  } = useDropMetadata({
    isDropMode,
    requiredMetadata
  });
  const metadataErrorById = useMemo(() => getIdentitySubmissionMetadataErrors({
    isIdentitySubmissionExperience: isIdentitySubmissionExperience && isDropMode,
    metadata
  }), [isDropMode, isIdentitySubmissionExperience, metadata]);
  const hasMetadataValidationErrors = Object.keys(metadataErrorById).length > 0;
  const hasMetadata = useMemo(() => hasMetadataContent(metadata), [metadata]);
  const getMarkdown = useMemo(() => editorState ? exportDropMarkdown(editorState, [...SAFE_MARKDOWN_TRANSFORMERS, MENTION_TRANSFORMER, HASHTAG_TRANSFORMER, WAVE_MENTION_TRANSFORMER, IMAGE_TRANSFORMER, EMOJI_TRANSFORMER]) : null, [editorState]);
  const isStormModeActive = isStormMode;
  const sendTyping = React.useCallback(() => {
    send(WsMessageType.USER_IS_TYPING, {
      wave_id: wave.id
    });
  }, [send, wave.id]);
  const throttleHandle = useMemo(() => {
    return throttle(sendTyping, 4000);
  }, [sendTyping]);
  useEffect(() => {
    if (!getMarkdown?.length) {
      return;
    }
    throttleHandle();
  }, [getMarkdown, throttleHandle]);
  const getCanSubmitStorm = () => {
    const markdown = getMarkdown;
    if (markdown?.length && markdown.length > 240) {
      return false;
    }
    return true;
  };
  const getCanSubmit = () => {
    const dropParts = drop?.parts ?? [];
    return hasSubmissionContent({
      markdown: getMarkdown,
      files,
      parts: dropParts,
      hasMetadata
    }) && !hasMetadataValidationErrors && !!(dropParts.length ? getCanSubmitStorm() : true);
  };
  const getHaveMarkdownOrFile = () => !!getMarkdown || files.length > 0;
  const getIsDropLimit = () => (drop?.parts.reduce((acc, part) => acc + (part.content?.length ?? 0), getMarkdown?.length ?? 0) ?? 0) >= 24000;
  const getCanAddPart = () => getHaveMarkdownOrFile() && !getIsDropLimit();
  const canSubmit = getCanSubmit();
  const canAddPart = getCanAddPart();
  const normalizedCurationDropUrl = useMemo(() => {
    if (!isCurationSubmissionExperience || isDropMode) {
      return null;
    }
    return normalizeCurationDropInput(getMarkdown ?? "");
  }, [getMarkdown, isCurationSubmissionExperience, isDropMode]);
  const showCurationDropModeWarning = !isDropMode && !!normalizedCurationDropUrl && isCurationSubmissionExperience;
  const [referencedNfts, setReferencedNfts] = useState<ReferencedNft[]>([]);
  const onReferencedNft = (newNft: ReferencedNft) => {
    setReferencedNfts([...referencedNfts.filter(i => !(i.token === newNft.token && i.contract === newNft.contract)), newNft]);
  };
  const [mentionedUsers, setMentionedUsers] = useState<Omit<MentionedUser, "current_handle">[]>([]);
  const onMentionedUser = (newUser: Omit<MentionedUser, "current_handle">) => {
    setMentionedUsers(curr => {
      return [...curr, newUser];
    });
  };
  const [mentionedWaves, setMentionedWaves] = useState<MentionedWave[]>([]);
  const onMentionedWave = (newWave: MentionedWave) => {
    setMentionedWaves(curr_0 => {
      return [...curr_0, newWave];
    });
  };
  const createDropInputRef = useRef<CreateDropInputHandles | null>(null);
  const isInitialMountRef = useRef(true);
  const identityValidationMessage = useMemo(() => {
    if (!isIdentitySubmissionExperience || !isDropMode) {
      return null;
    }
    if (identitySubmissionMode === ApiWaveParticipationIdentitySubmissionWhoCanBeSubmitted.OnlyMyself) {
      return viewerSelectableIdentity ? null : "We couldn't determine your identity for this submission.";
    }
    if (!selectedIdentity) {
      return "Select an identity to nominate.";
    }
    if (identitySubmissionMode === ApiWaveParticipationIdentitySubmissionWhoCanBeSubmitted.OnlyOthers && normalizeIdentityValue(selectedIdentity) === normalizeIdentityValue(viewerSelectableIdentity)) {
      return SELECT_OTHER_IDENTITY_ERROR;
    }
    return null;
  }, [identitySubmissionMode, isDropMode, isIdentitySubmissionExperience, selectedIdentity, viewerSelectableIdentity]);
  const showIdentityValidationMessage = !!identityValidationMessage && (hasAttemptedIdentitySubmit || selectedIdentity !== null && identitySubmissionMode === ApiWaveParticipationIdentitySubmissionWhoCanBeSubmitted.OnlyOthers || identitySubmissionMode === ApiWaveParticipationIdentitySubmissionWhoCanBeSubmitted.OnlyMyself);
  const getSubmissionMetadata = useCallback(() => {
    return buildDropSubmissionMetadata({
      metadata,
      identity: isIdentitySubmissionExperience && isDropMode ? selectedIdentity : null
    });
  }, [isDropMode, isIdentitySubmissionExperience, metadata, selectedIdentity]);
  const getReplyTo = () => {
    if (activeDrop?.action === ActiveDropAction.REPLY) {
      return {
        drop_id: activeDrop.drop.id,
        drop_part_id: activeDrop.partId
      };
    }
    return undefined;
  };
  const getInitialDrop = (): CreateDropConfig | null => {
    const markdown_0 = getMarkdown;
    if (shouldUseInitialDropConfig(markdown_0, files.length)) {
      const baseParts = drop?.parts.length ? drop.parts : [];
      const replyTo = getReplyTo();
      const replyToObj = replyTo ? {
        reply_to: replyTo
      } : {};
      return {
        title: null,
        ...replyToObj,
        parts: ensurePartsWithFallback(baseParts, hasMetadata),
        mentioned_users: drop?.mentioned_users ?? [],
        mentioned_waves: drop?.mentioned_waves ?? [],
        referenced_nfts: drop?.referenced_nfts ?? [],
        metadata: getSubmissionMetadata(),
        signature: null,
        drop_type: isDropMode ? ApiDropType.Participatory : ApiDropType.Chat,
        is_safe_signature: isSafeWallet,
        signer_address: address ?? ""
      };
    }
    return null;
  };
  const replyTo_0 = getReplyTo();
  const replyToObj_0 = replyTo_0 ? {
    reply_to: replyTo_0
  } : {};
  const createGifDrop = (gif: string): CreateDropConfig => {
    return {
      title: null,
      drop_type: isDropMode ? ApiDropType.Participatory : ApiDropType.Chat,
      ...replyToObj_0,
      parts: [...(drop?.parts ?? []), {
        content: gif,
        quoted_drop: activeDrop?.action === ActiveDropAction.QUOTE ? {
          drop_id: activeDrop.drop.id,
          drop_part_id: activeDrop.partId
        } : null,
        media: files
      }],
      mentioned_users: [],
      mentioned_waves: [],
      referenced_nfts: [],
      metadata: getSubmissionMetadata(),
      signature: null,
      is_safe_signature: isSafeWallet,
      signer_address: address ?? ""
    };
  };
  const createCurrentDrop = (markdown_1: string | null, allMentions: ApiDropMentionedUser[], allNfts: ReferencedNft[], allWaves: ApiMentionedWave[]): CreateDropConfig => {
    const availableFiles = files;
    const hasPartsInDrop = (drop?.parts.length ?? 0) > 0;
    const hasCurrentContent = hasCurrentDropPartContent(markdown_1, availableFiles.length);
    const quotedDrop = activeDrop?.action === ActiveDropAction.QUOTE ? {
      drop_id: activeDrop.drop.id,
      drop_part_id: activeDrop.partId
    } : null;
    const nonCurationParts = hasPartsInDrop && !hasCurrentContent ? drop?.parts ?? [] : [...(drop?.parts ?? []), {
      content: markdown_1?.length ? markdown_1 : null,
      quoted_drop: quotedDrop,
      media: availableFiles
    }];
    const parts = ensurePartsWithFallback(nonCurationParts, hasMetadata);
    const replyTo_1 = getReplyTo();
    const replyToObj_1 = replyTo_1 ? {
      reply_to: replyTo_1
    } : {};
    return {
      title: null,
      drop_type: isDropMode ? ApiDropType.Participatory : ApiDropType.Chat,
      ...replyToObj_1,
      parts,
      mentioned_users: allMentions,
      mentioned_waves: allWaves,
      referenced_nfts: allNfts,
      metadata: getSubmissionMetadata(),
      signature: null,
      is_safe_signature: isSafeWallet,
      signer_address: address ?? ""
    };
  };
  const getUpdatedDrop = (): CreateDropConfig => {
    const initialDrop = getInitialDrop();
    if (initialDrop) {
      return initialDrop;
    }
    const markdown_2 = getMarkdown;
    const existingMentions = drop?.mentioned_users ?? [];
    const existingNfts = drop?.referenced_nfts ?? [];
    const existingWaves = drop?.mentioned_waves ?? [];
    const {
      updatedMentions,
      updatedNfts,
      updatedWaves,
      updatedMarkdown
    } = handleDropPart(markdown_2, existingMentions, existingNfts, existingWaves, mentionedUsers, referencedNfts, mentionedWaves);
    return createCurrentDrop(updatedMarkdown, updatedMentions, updatedNfts, updatedWaves);
  };
  const updateDropStateAndClearInput = (newDrop: CreateDropConfig) => {
    setDrop(newDrop);
    createDropInputRef.current?.clearEditorState();
    setFiles([]);
  };
  const finalizeAndAddDropPart = (): CreateDropConfig => {
    const updatedDrop = getUpdatedDrop();
    updateDropStateAndClearInput(updatedDrop);
    return updatedDrop;
  };
  const filterMentionedUsers = ({
    mentionedUsers: mentionedUsers_0,
    parts: parts_0
  }: {
    readonly mentionedUsers: ApiDropMentionedUser[];
    readonly parts: CreateDropPart[];
  }): ApiDropMentionedUser[] => mentionedUsers_0.filter(user => parts_0.some(part_0 => part_0.content?.includes(`@[${user.handle_in_content}]`)));
  const filterMentionedWaves = ({
    mentionedWaves: mentionedWavesList,
    parts: parts_1
  }: {
    readonly mentionedWaves: ApiMentionedWave[];
    readonly parts: CreateDropPart[];
  }): ApiMentionedWave[] => mentionedWavesList.filter(w => parts_1.some(part_1 => part_1.content?.includes(`#[${w.wave_name_in_content}]`)));
  const [dropEditorRefreshKey, setDropEditorRefreshKey] = useState(0);
  const refreshState = () => {
    createDropInputRef.current?.clearEditorState();
    setEditorState(null);
    setMetadata(initialMetadata);
    setMentionedUsers([]);
    setMentionedWaves([]);
    setReferencedNfts([]);
    setDrop(null);
    setMetadataOpenState(null);
    setSelectedIdentityState(null);
    setHasAttemptedIdentitySubmitState(null);
    setIdentityPickerOpenState(null);
    setIdentityPickerErrorMessageState(null);
    setShowOptionsState(null);
    closeOnNextInputRef.current = false;
    setDropEditorRefreshKey(prev_2 => prev_2 + 1);
  };
  const getUpdatedDropRequest = async (requestBody: ApiCreateDropRequest): Promise<ApiCreateDropRequest | null> => {
    if (requestBody.drop_type === ApiDropType.Chat) {
      return requestBody;
    }
    if (!wave.participation.signature_required) {
      return requestBody;
    }

    // Use direct signature if there are no terms to display
    if (!wave.participation.terms) {
      const {
        success,
        signature
      } = await signDrop({
        drop: requestBody,
        termsOfService: null
      });
      if (!success || !signature) {
        return null;
      }
      return {
        ...requestBody,
        signature
      };
    }

    // For terms that need to be displayed, use the terms flow
    return new Promise<ApiCreateDropRequest | null>(resolve => {
      // Define callback for when signing completes
      const handleSigningComplete = (result: {
        success: boolean;
        signature?: string | undefined;
      }) => {
        if (!result.success || !result.signature) {
          resolve(null);
          return;
        }
        const updatedDropRequest = {
          ...requestBody,
          signature: result.signature
        };
        resolve(updatedDropRequest);
      };

      // Show the terms modal through a global event
      const event = new CustomEvent("showTermsModal", {
        detail: {
          drop: requestBody,
          termsOfService: wave.participation.terms,
          onComplete: handleSigningComplete
        }
      });
      document.dispatchEvent(event);
    });
  };
  const prepareAndSubmitDrop = async (dropRequest: CreateDropConfig) => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    const {
      success: success_0
    } = await requestAuth();
    if (!success_0) {
      setSubmitting(false);
      return;
    }
    if (!dropRequest.parts.length) {
      setSubmitting(false);
      return;
    }
    try {
      const parts_2 = await generateParts(dropRequest.parts, setUploadingFiles);
      if (!parts_2.length) {
        setSubmitting(false);
        return;
      }
      const requestBody_0: ApiCreateDropRequest = {
        ...dropRequest,
        mentioned_users: filterMentionedUsers({
          mentionedUsers: dropRequest.mentioned_users,
          parts: dropRequest.parts
        }),
        mentioned_waves: filterMentionedWaves({
          mentionedWaves: dropRequest.mentioned_waves ?? [],
          parts: dropRequest.parts
        }),
        metadata: dropRequest.metadata,
        wave_id: wave.id,
        parts: parts_2
      };
      const updatedDropRequest_0 = await getUpdatedDropRequest(requestBody_0);
      if (!updatedDropRequest_0) {
        setSubmitting(false);
        return;
      }
      const optimisticDrop = getOptimisticDrop(updatedDropRequest_0, connectedProfile, wave, activeDrop, isDropMode ? ApiDropType.Participatory : ApiDropType.Chat);
      if (optimisticDrop) {
        addOptimisticDrop({
          drop: optimisticDrop
        });
        setTimeout(() => processIncomingDrop(optimisticDrop, ProcessIncomingDropType.DROP_INSERT), 0);
      }
      !!getMarkdown?.length && createDropInputRef.current?.clearEditorState();
      (document.activeElement as HTMLElement).blur();
      if (isApp) {
        import("@capacitor/core").then(({
          Capacitor
        }) => {
          if (Capacitor.getPlatform() === "android") {
            import("@capacitor/keyboard").then(({
              Keyboard
            }) => {
              Keyboard.hide().catch(() => {});
            });
          }
        });
      }
      setFiles([]);
      if (isIdentityPickerAllowed) {
        setIdentityPickerAutoOpenState({
          scopeKey: identitySubmissionSessionScopeKey,
          value: false
        });
      }
      refreshState();
      submitDrop({
        drop: updatedDropRequest_0,
        dropId: optimisticDrop?.id ?? null,
        onSuccess: isDropMode && canExitDropMode ? () => handleDropModeChange(false) : undefined,
        onError: isDropMode && canExitDropMode ? handleDuplicateIdentitySubmissionError : undefined
      });
    } catch (error) {
      setToast({
        message: error instanceof Error ? error.message : String(error),
        type: "error"
      });
    } finally {
      setSubmitting(false);
    }
  };
  const getMissingRequirementsResult = useMemo(() => {
    return () => getMissingRequirements(isDropMode, metadata, files, wave.participation.required_media);
  }, [metadata, files, wave.participation.required_media, isDropMode]);
  const [missingRequirements, setMissingRequirements] = useState<MissingRequirements>({
    metadata: [],
    media: []
  });
  useEffect(() => {
    setMissingRequirements(getMissingRequirementsResult());
  }, [metadata, files, getMissingRequirementsResult]);
  const onDrop = async (): Promise<void> => {
    if (submitting) {
      return;
    }
    if (identityValidationMessage) {
      setHasAttemptedIdentitySubmitState({
        scopeKey: identitySubmissionSessionScopeKey,
        value: true
      });
      return;
    }
    if (hasMetadataValidationErrors) {
      setMetadataOpenState({
        scopeKey: dropModeSessionScopeKey,
        value: true
      });
      return;
    }
    if (missingRequirements.metadata.length || missingRequirements.media.length) {
      return;
    }
    const hasPartsInDrop_0 = (drop?.parts.length ?? 0) > 0;
    const hasCurrentContent_0 = (getMarkdown?.trim().length ?? 0) > 0 || files.length > 0;
    if (hasPartsInDrop_0 && hasCurrentContent_0) {
      finalizeAndAddDropPart();
      return;
    }
    await prepareAndSubmitDrop(getUpdatedDrop());
  };
  const onGifDrop = async (gif_0: string): Promise<void> => {
    if (submitting) {
      return;
    }
    if (identityValidationMessage) {
      setHasAttemptedIdentitySubmitState({
        scopeKey: identitySubmissionSessionScopeKey,
        value: true
      });
      return;
    }
    if (hasMetadataValidationErrors) {
      setMetadataOpenState({
        scopeKey: dropModeSessionScopeKey,
        value: true
      });
      return;
    }
    await prepareAndSubmitDrop(createGifDrop(gif_0));
  };
  const onSwitchToDropMode = useCallback(() => {
    if (!normalizedCurationDropUrl) {
      return;
    }
    onSwitchToDropModeWithUrl(normalizedCurationDropUrl);
  }, [normalizedCurationDropUrl, onSwitchToDropModeWithUrl]);
  const focusInputWithDelay = (delay: number) => {
    setTimeout(() => {
      createDropInputRef.current?.focus();
    }, delay);
  };
  const focusMobileInput = useCallback(() => {
    if (!createDropInputRef.current) return;
    requestAnimationFrame(() => {
      focusInputWithDelay(300);
    });
  }, []);
  const focusDesktopInput = () => {
    createDropInputRef.current?.focus();
  };
  useEffect(() => {
    if (!activeDrop) {
      return;
    }

    // Skip auto-focus on initial mount in app to prevent keyboard from opening
    if (isApp && isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }
    isInitialMountRef.current = false;
    if (isApp) {
      const timer = setTimeout(focusMobileInput, 200);
      return () => clearTimeout(timer);
    }
    const timer_0 = setTimeout(() => {
      focusDesktopInput();
    }, 100);
    return () => clearTimeout(timer_0);
  }, [activeDrop, isApp, focusMobileInput]);
  const handleFileChange = (newFiles: File[]) => {
    let updatedFiles = [...files, ...newFiles];
    let removedCount = 0;
    if (updatedFiles.length > MAX_DROP_UPLOAD_FILES) {
      removedCount = updatedFiles.length - MAX_DROP_UPLOAD_FILES;
      updatedFiles = updatedFiles.slice(-MAX_DROP_UPLOAD_FILES);
      setToast({
        message: `File limit exceeded. The ${removedCount} oldest file${removedCount > 1 ? "s were" : " was"} removed to maintain the ${MAX_DROP_UPLOAD_FILES}-file limit. New files have been added.`,
        type: "warning"
      });
    }
    setFiles(updatedFiles);
    if (!isWideContainer) {
      setShowOptionsState({
        scopeKey: wave.id,
        value: false
      });
      closeOnNextInputRef.current = false;
    }
  };
  const handleSetShowOptions = useCallback((next: boolean) => {
    hasUserToggledOptionsRef.current = true;
    setShowOptionsState({
      scopeKey: wave.id,
      value: next
    });
    if (isWideContainer) {
      closeOnNextInputRef.current = false;
      return;
    }
    closeOnNextInputRef.current = next;
  }, [isWideContainer, wave.id]);
  const handleEditorStateChange = useCallback((newEditorState: EditorState) => {
    setEditorState(newEditorState);
    if (!isWideContainer && closeOnNextInputRef.current) {
      setShowOptionsState({
        scopeKey: wave.id,
        value: false
      });
      closeOnNextInputRef.current = false;
    }
  }, [isWideContainer, wave.id]);
  const handleEditorBlur = useCallback((event_0: React.FocusEvent<HTMLDivElement>) => {
    if (isWideContainer) {
      return;
    }
    const nextTarget = event_0.relatedTarget as Node | null;
    if (nextTarget && actionsContainerRef.current?.contains(nextTarget)) {
      return;
    }
    setShowOptionsState({
      scopeKey: wave.id,
      value: false
    });
    closeOnNextInputRef.current = false;
  }, [isWideContainer, wave.id]);
  const removeFile = (file: File, partIndex?: number) => {
    if (partIndex === undefined) {
      // Remove file from the current files array
      setFiles(prevFiles => prevFiles.filter(f => f !== file));
    } else {
      // Remove file from a specific part
      setDrop(prevDrop => {
        if (!prevDrop) return null;
        const newParts = [...prevDrop.parts];
        const part_2 = newParts[partIndex];
        if (!part_2) return prevDrop;
        newParts[partIndex] = {
          ...part_2,
          media: part_2.media.filter(f_0 => f_0 !== file)
        };
        return {
          ...prevDrop,
          parts: newParts
        };
      });
    }
  };
  useEffect(() => {
    if (!drop) {
      setIsStormMode(false);
      return;
    }
    if (!drop.parts.length) {
      setIsStormMode(false);
    }
  }, [drop, setIsStormMode]);
  const openMetadata = useCallback(() => {
    setMetadataOpenState({
      scopeKey: dropModeSessionScopeKey,
      value: true
    });
  }, [dropModeSessionScopeKey]);
  const closeMetadata = useCallback(() => {
    setMetadataOpenState({
      scopeKey: dropModeSessionScopeKey,
      value: false
    });
  }, [dropModeSessionScopeKey]);
  const handleDropModeChange = useCallback((newIsDropMode: boolean) => {
    if (!newIsDropMode) {
      closeMetadata();
    }
    onDropModeChange(newIsDropMode);
  }, [closeMetadata, onDropModeChange]);
  const openIdentityPicker = useCallback(() => {
    setIdentityPickerErrorMessageState({
      scopeKey: identitySubmissionSessionScopeKey,
      value: null
    });
    setIdentityPickerAutoOpenState({
      scopeKey: identitySubmissionSessionScopeKey,
      value: false
    });
    setIdentityPickerOpenState({
      scopeKey: identitySubmissionSessionScopeKey,
      value: true
    });
  }, [identitySubmissionSessionScopeKey]);
  const closeIdentityPicker = useCallback(() => {
    if (!selectedIdentitySelection && !canExitDropMode) {
      return;
    }
    setIdentityPickerAutoOpenState({
      scopeKey: identitySubmissionSessionScopeKey,
      value: false
    });
    setIdentityPickerOpenState({
      scopeKey: identitySubmissionSessionScopeKey,
      value: false
    });
    setIdentityPickerErrorMessageState({
      scopeKey: identitySubmissionSessionScopeKey,
      value: null
    });
    if (!selectedIdentitySelection) {
      handleDropModeChange(false);
    }
  }, [canExitDropMode, handleDropModeChange, identitySubmissionSessionScopeKey, selectedIdentitySelection]);
  const closeIdentitySelectionPanel = useCallback(() => {
    setIdentityPickerAutoOpenState({
      scopeKey: identitySubmissionSessionScopeKey,
      value: false
    });
    setIdentityPickerOpenState({
      scopeKey: identitySubmissionSessionScopeKey,
      value: false
    });
    setIdentityPickerErrorMessageState({
      scopeKey: identitySubmissionSessionScopeKey,
      value: null
    });
    handleDropModeChange(false);
  }, [handleDropModeChange, identitySubmissionSessionScopeKey]);
  const handleDuplicateIdentitySubmissionError = useCallback((error_0: unknown) => {
    if (!canExitDropMode) {
      return;
    }
    const message = error_0 instanceof Error ? error_0.message.toLowerCase() : String(error_0).toLowerCase();
    const isDuplicateIdentityError = message.includes("identity") && (message.includes("already been voted") || message.includes("already voted") || message.includes("already been nominated") || message.includes("already nominated"));
    if (isDuplicateIdentityError) {
      handleDropModeChange(false);
    }
  }, [canExitDropMode, handleDropModeChange]);
  const handleIdentitySelection = useCallback((selection: SelectableIdentityOption) => {
    if (identitySubmissionMode === ApiWaveParticipationIdentitySubmissionWhoCanBeSubmitted.OnlyOthers && normalizeIdentityValue(selection.value) === normalizeIdentityValue(viewerSelectableIdentity)) {
      setIdentityPickerErrorMessageState({
        scopeKey: identitySubmissionSessionScopeKey,
        value: SELECT_OTHER_IDENTITY_ERROR
      });
      return;
    }
    setSelectedIdentityState({
      scopeKey: identitySubmissionSessionScopeKey,
      value: selection
    });
    setIdentityPickerAutoOpenState({
      scopeKey: identitySubmissionSessionScopeKey,
      value: false
    });
    setHasAttemptedIdentitySubmitState({
      scopeKey: identitySubmissionSessionScopeKey,
      value: false
    });
    setIdentityPickerErrorMessageState({
      scopeKey: identitySubmissionSessionScopeKey,
      value: null
    });
    setIdentityPickerOpenState({
      scopeKey: identitySubmissionSessionScopeKey,
      value: false
    });
  }, [identitySubmissionMode, identitySubmissionSessionScopeKey, viewerSelectableIdentity]);
  const onChangeKey = (params: {
    index: number;
    newKey: string;
  }) => {
    setMetadata(prev_3 => prev_3.map((item_0, i_0) => i_0 === params.index ? {
      ...item_0,
      key: params.newKey
    } : item_0));
  };
  const onChangeValue = (params_0: {
    index: number;
    newValue: string | number | null;
  }) => {
    setMetadata(prev_4 => prev_4.map((item_1, i_1) => {
      if (i_1 !== params_0.index) return item_1;
      if (item_1.type === ApiWaveMetadataType.Number) {
        if (params_0.newValue === null || params_0.newValue === "") {
          return {
            ...item_1,
            value: null
          };
        }
        const parsedValue = Number(params_0.newValue);
        return {
          ...item_1,
          value: Number.isNaN(parsedValue) ? null : parsedValue
        };
      }
      if (item_1.type === ApiWaveMetadataType.String) {
        if (typeof params_0.newValue === "string") {
          return {
            ...item_1,
            value: params_0.newValue
          };
        }
        return {
          ...item_1,
          value: String(params_0.newValue)
        };
      }
      return item_1;
    }));
  };
  const onAddMetadata = () => {
    setMetadata([...metadata, {
      id: generateMetadataId(),
      key: "",
      type: null,
      value: null,
      required: false
    }]);
  };
  const onRemoveMetadata = (index: number) => {
    setMetadata(prev_5 => {
      const newMetadata = [...prev_5];
      newMetadata.splice(index, 1);
      return newMetadata;
    });
  };
  const breakIntoStorm = () => {
    finalizeAndAddDropPart();
    setIsStormMode(true);
  };

  // Clear active reply/quote when entering edit mode on mobile
  useEffect(() => {
    if (isApp && editingDropId && activeDrop) {
      onCancelReplyQuote();
    }
  }, [isApp, editingDropId, activeDrop, onCancelReplyQuote]);
  const isChatClosed = wave.wave.type === ApiWaveType.Chat && !wave.chat.enabled;
  if (isChatClosed) {
    return <div className="tw-w-full tw-flex-grow tw-rounded-lg tw-bg-iron-900 tw-p-4 tw-text-center tw-text-sm tw-font-medium tw-text-iron-500">
        Wave is closed
      </div>;
  }
  return <div className="tw-flex-grow">
      <CreateDropReplyingWrapper activeDrop={activeDrop} submitting={submitting} onCancelReplyQuote={onCancelReplyQuote} dropId={dropId} />
      {isIdentitySubmissionExperience && isDropMode && identitySubmissionMode !== null && <CreateDropIdentityField mode={identitySubmissionMode} selectedIdentity={selectedIdentitySelection} selfIdentity={viewerIdentity} disabled={submitting} errorMessage={showIdentityValidationMessage ? identityValidationMessage : null} onOpenPicker={openIdentityPicker} onClosePanel={canExitDropMode && dropModeToggleExitLabel === null ? closeIdentitySelectionPanel : undefined} />}
      {isIdentitySubmissionExperience && isDropMode && identitySubmissionMode !== null && identitySubmissionMode !== ApiWaveParticipationIdentitySubmissionWhoCanBeSubmitted.OnlyMyself && <CreateDropIdentityPickerModal isOpen={isIdentityPickerOpen} mode={identitySubmissionMode} selectedIdentity={selectedIdentitySelection} disabled={submitting} errorMessage={identityPickerErrorMessage} canClose={canDismissIdentityPicker} onClose={closeIdentityPicker} onSelect={handleIdentitySelection} />}
      <div className="tw-flex tw-w-full tw-items-end">
        <div ref={actionsContainerRef} className="tw-flex tw-w-full tw-items-center tw-gap-x-2 lg:tw-gap-x-3">
          <CreateDropActions isStormMode={isStormModeActive} isDropMode={isDropMode} canAddPart={canAddPart} submitting={submitting} showOptions={showOptions} animateOptions={!isWideContainer && hasUserToggledOptionsRef.current} isRequiredMetadataMissing={!!missingRequirements.metadata.length} isRequiredMediaMissing={!!missingRequirements.media.length} handleFileChange={handleFileChange} onAddMetadataClick={openMetadata} breakIntoStorm={breakIntoStorm} setShowOptions={handleSetShowOptions} onGifDrop={onGifDrop} />
          <div className="tw-w-full tw-flex-grow">
            <CreateDropInput waveId={wave.id} key={dropEditorRefreshKey} ref={createDropInputRef} editorState={editorState} type={activeDrop?.action ?? null} submitting={submitting} isStormMode={isStormModeActive} isDropMode={isDropMode} canSubmit={canSubmit} onEditorState={handleEditorStateChange} onEditorBlur={handleEditorBlur} onReferencedNft={onReferencedNft} onMentionedUser={onMentionedUser} onMentionedWave={onMentionedWave} onDrop={onDrop} />
            {showCurationDropModeWarning && <div className="tw-mt-2 tw-text-[11px] tw-leading-4 tw-text-amber-200/90">
                This looks like a curation URL.{" "}
                <button type="button" className="tw-border-0 tw-bg-transparent tw-p-0 tw-text-[11px] tw-font-medium tw-text-amber-300 tw-underline tw-transition desktop-hover:hover:tw-text-amber-100" onClick={onSwitchToDropMode}>
                  Switch to Drop mode
                </button>
              </div>}
          </div>
        </div>
        <div className="tw-ml-2 lg:tw-ml-3">
          <div className="tw-flex tw-items-center tw-gap-x-3">
            {showDropModeToggle && isParticipatory && !dropId && submissionExperience !== WaveSubmissionExperience.MEMES_LEGACY && <CreateDropDropModeToggle isDropMode={isDropMode} onDropModeChange={handleDropModeChange} privileges={privileges} exitLabel={dropModeToggleExitLabel} />}
            <CreateDropSubmit submitting={submitting} canSubmit={canSubmit} onDrop={onDrop} isDropMode={isDropMode} />
          </div>
        </div>
      </div>
      {isDropMode && <CreateDropContentRequirements canSubmit={canSubmit} wave={wave} missingMedia={missingRequirements.media} missingMetadata={missingRequirements.metadata} onOpenMetadata={openMetadata} setFiles={handleFileChange} disabled={submitting} />}
      <AnimatePresence>
        {isDropMode && isMetadataOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: "auto"
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.3
      }}>
            <CreateDropMetadata disabled={submitting} onRemoveMetadata={onRemoveMetadata} closeMetadata={closeMetadata} metadata={metadata} missingRequiredMetadataKeys={missingRequirements.metadata} metadataErrorById={metadataErrorById} onChangeKey={onChangeKey} onChangeValue={onChangeValue} onAddMetadata={onAddMetadata} />
          </motion.div>}
      </AnimatePresence>
      <CreateDropContentFiles parts={drop?.parts ?? []} files={files} uploadingFiles={uploadingFiles} removeFile={removeFile} disabled={submitting} />
      <TermsSignatureFlow />
    </div>;
};
export default memo(CreateDropContent);